import { useState } from "react";
import axios from "axios";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MessageSquareHeart, Lightbulb, Bug, Heart, MessageCircle } from "lucide-react";

const CATEGORIES = [
  { key: "idea", label: "Idea", icon: Lightbulb },
  { key: "problem", label: "Problem", icon: Bug },
  { key: "praise", label: "Praise", icon: Heart },
  { key: "other", label: "Other", icon: MessageCircle },
];

const residentAuth = () => ({ withCredentials: true });

/** One feedback funnel for both consoles: managers (dashboard) and residents (portal). */
export const FeedbackWidget = ({ portal = false, trigger }) => {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("idea");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!message.trim()) { toast.error("Say a little something first"); return; }
    setSending(true);
    const body = { message: message.trim(), category, page: window.location.pathname };
    try {
      if (portal) await axios.post(`${API}/resident/feedback`, body, residentAuth());
      else await api.post("/feedback", body);
      toast.success("Thanks — your feedback went straight to the Redline team 🙌");
      setOpen(false); setMessage(""); setCategory("idea");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send that — try again");
    } finally { setSending(false); }
  };

  return (
    <>
      {trigger ? trigger(() => setOpen(true)) : (
        <button data-testid="feedback-open-btn" onClick={() => setOpen(true)} title="Give feedback"
          className={portal
            ? "flex items-center gap-1.5 text-sand-300 hover:text-white font-semibold text-sm"
            : "flex items-center gap-2 rounded-full border border-sand-200 bg-white px-4 py-2.5 text-sm font-semibold text-ink hover:border-ink transition-colors shrink-0"}>
          <MessageSquareHeart className={portal ? "h-4 w-4" : "h-4 w-4 text-redline-500"} strokeWidth={1.75} />
          <span className={portal ? "hidden sm:inline" : "hidden md:inline"}>Feedback</span>
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <MessageSquareHeart className="h-5 w-5 text-redline-500" strokeWidth={1.75} /> Help us make this better
            </DialogTitle>
            <DialogDescription>
              Ideas, gripes, love notes — it all goes straight to the team building {portal ? "your concierge" : "Ask Redline"}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2 flex-wrap">
              {CATEGORIES.map((c) => (
                <button key={c.key} data-testid={`feedback-cat-${c.key}`} onClick={() => setCategory(c.key)}
                  className={`flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold border transition-colors ${
                    category === c.key ? "bg-redline-500 text-white border-redline-500" : "bg-white text-ink-700 border-sand-200 hover:border-ink"}`}>
                  <c.icon className="h-3.5 w-3.5" strokeWidth={2} /> {c.label}
                </button>
              ))}
            </div>
            <textarea data-testid="feedback-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
              placeholder={portal ? "What would make the concierge more helpful for you?" : "What should we build, fix, or never touch again?"}
              className="w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 resize-y" />
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-full px-5 py-2.5 font-semibold text-ink-700 hover:bg-sand-100 transition-colors">Cancel</button>
            <button data-testid="feedback-submit-btn" disabled={sending} onClick={submit}
              className="rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 hover:bg-redline-600 transition-colors disabled:opacity-60">
              {sending ? "Sending…" : "Send feedback"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
